import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { resubscribeIfGranted } from '../../services/push.js';
import { useAuth } from '../../hooks/useAuth.js';
import RoleBadge from './RoleBadge.jsx';
import NotificationBell from './NotificationBell.jsx';
import SearchModal from '../common/SearchModal.jsx';
import ThemeToggle from '../common/ThemeToggle.jsx';
import LanguagePicker from '../common/LanguagePicker.jsx';
import FeedbackButton from '../feedback/FeedbackButton.jsx';
import { useSidebar } from '../../hooks/useSidebar.jsx';
import { useProductContext } from '../../hooks/useProductContext.jsx';
import ProjectChat from '../chat/ProjectChat.jsx';
import { useChatUnread } from '../../hooks/useChatUnread.jsx';
import AICopilotChat from '../ai/AICopilotChat.jsx';
import { useTranslation } from 'react-i18next'

function Navigation() {
  const { t: tch } = useTranslation('chrome')
  const { user, logout } = useAuth();
  // Inside a product, somebody is who THAT product says they are — a Consultant here can be
  // their organisation's Admin there. Reading the wrong one of those in the corner of the
  // screen is how people mistake what they are allowed to do.
  const { productRole, productName } = useProductContext();
  const shownRole = productRole || user?.display_role || user?.role;
  // A Partner exists only in a connected product. `display_role` has ALREADY been swapped to
  // their role there, so it must never be used to say what they are in ProTrack — that reads
  // "Admin in ProTrack" about somebody ProTrack grants nothing to. Their real role here is the
  // raw one, and the honest thing to tell them is that they have none of their own.
  const isPartner = user?.role === 'Partner';
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { unread: chatUnread, refresh: refreshChatUnread } = useChatUnread();
  // Manual refresh — reloads the app so users pull the latest data AND pick up a new deploy
  // (asks the service worker to update first, so the installed PWA gets fresh code too).
  const handleRefresh = async () => {
    setRefreshing(true);
    try { const reg = await navigator.serviceWorker?.getRegistration(); await reg?.update(); } catch { /* ignore */ }
    window.location.reload();
  };
  // Show/hide the floating AI agent — PER USER (a person's choice is their own, not shared).
  const agentKey = `agent3p-hidden:${user?.id || user?.email || 'anon'}`;
  const [agentHidden, setAgentHidden] = useState(false);
  useEffect(() => { setAgentHidden(localStorage.getItem(agentKey) === '1'); }, [agentKey]);
  const toggleAgent = () => {
    const next = !agentHidden;
    localStorage.setItem(agentKey, next ? '1' : '0');
    setAgentHidden(next);
    window.dispatchEvent(new Event('agent3p-visibility'));
  };
  const navigate = useNavigate();
  const { isMobile, openMobile } = useSidebar();
  const menuRef = useRef(null); // Ref for the dropdown menu

  // If this device already granted notification permission, keep the backend's push
  // subscription for it fresh on login (no prompt — silent no-op if not granted).
  useEffect(() => { if (user) resubscribeIfGranted(user.id); }, [user?.id]);

  // Global keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    setShowUserMenu(false);
    logout();
    navigate('/login');
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
        <div className="px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            {/* Left side: Hamburger + Search */}
            <div className="flex items-center gap-2">
              {/* Hamburger Menu (Mobile) */}
              {isMobile && (
                <button
                  onClick={openMobile}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors md:hidden"
                  aria-label={tch('aria_open_menu')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}

              {/* Search Button */}
              <button
                onClick={() => setShowSearch(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden md:inline">{tch('ph_search')}</span>
                <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-gray-400 bg-white round border border-gray-200">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </button>
            </div>

            <div className="flex items-center space-x-3">
              {/* Refresh — reload latest data & pick up new app versions (helps installed PWA) */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="relative p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60"
                aria-label={tch('aria_refresh')}
                title={tch('t_refresh_long')}
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Feedback / Suggestions / Issues — files a GitHub issue */}
              <FeedbackButton />

              {/* Show / hide the floating AI agent */}
              <button
                onClick={toggleAgent}
                className={`relative p-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  agentHidden ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' : 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
                }`}
                aria-label={agentHidden ? 'Show AI Agent' : 'Hide AI Agent'}
                title={agentHidden ? 'Show AI Agent' : 'Hide AI Agent'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                </svg>
                {agentHidden && <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-gray-400 rounded-full ring-2 ring-white" />}
              </button>

              {/* Chat button — beside notification bell, with unread badge */}
              {['Admin', 'Project Manager', 'Consultant', 'Finance', 'Funder'].includes(user?.role) && (
                <button
                  onClick={() => { setShowChat(c => !c); setTimeout(refreshChatUnread, 800); }}
                  className={`relative p-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    showChat ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label={tch('aria_project_chat')}
                  title={tch('aria_project_chat')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {chatUnread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-4.5 h-4.5 px-1 text-[10px] font-bold text-white bg-red-500 rounded-full ring-2 ring-white animate-pulse">
                      {chatUnread > 9 ? '9+' : chatUnread}
                    </span>
                  )}
                </button>
              )}

              {/* Language — one of the six UN languages. Sits in the chrome rather than buried in
                  Preferences: somebody who cannot read the interface cannot navigate to the
                  page where they would change it. */}
              <LanguagePicker compact />

              {/* Notifications */}
              {!productRole && <NotificationBell />}

              {/* Vertical Separator */}
              <div className="h-6 w-px bg-gray-200"></div>

              {/* User Menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 text-sm rounded-lg py-1 px-2 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center ring-1 ring-white">
                    <span className="text-xs font-medium text-white">
                      {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                    </span>
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="font-medium text-gray-800 text-sm">{user?.name}</p>
                    <p className="text-xs text-gray-500">{shownRole}</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      <div className="mt-1.5">
                        <RoleBadge role={shownRole} size="sm" />
                      </div>
                      {/* Both, plainly: who they are here, and who they are in ProTrack. The
                          badge changing without saying why would read as an account change. */}
                      {isPartner ? (
                        <p className="text-[10px] text-gray-400 mt-1">
                          {productName ? `in ${productName} · ` : ''}no separate ProTrack role
                        </p>
                      ) : productName && (
                        <p className="text-[10px] text-gray-400 mt-1">
                          {/* A product role belongs to that product. Where it granted none, say
                              so — a Super Admin standing here is ProTrack's, and printing it as
                              theirs "in {productName}" is how the two got confused. */}
                          {productRole ? `in ${productName}` : `no role in ${productName}`}
                          {' · '}{user?.display_role || user?.role} in ProTrack
                        </p>
                      )}
                    </div>

                    <div className="py-1">
                      <Link
                        to="/profile-settings"
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <svg className="w-4 h-4 mr-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                        Profile Settings
                      </Link>
                      <Link
                        to="/preferences"
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <svg className="w-4 h-4 mr-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        Preferences
                      </Link>
                    </div>

                    <div className="border-t border-gray-100">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <svg className="w-4 h-4 mr-2.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Search Modal */}
      <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />

      {/* Project Chat floating panel */}
      <ProjectChat open={showChat} onClose={() => setShowChat(false)} />

      {/* AI Copilot floating button (bottom-left) */}
      <AICopilotChat />
    </>
  );
}

export default Navigation;
